import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Input, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';
import {
  QUERY_ACCOUNT_BY_ROLE_PAGE,
  type QueryAccountByRolePageResponse,
  type QueryAccountByRolePageVariables,
  type OpdsAccountType,
} from '../graphql/queries/queryAccountByRolePage';

export interface RoleAccountsDrawerProps {
  visible: boolean;
  roleId: number | string;
  roleName: string;
  onClose: () => void;
}

interface AccountFilters {
  id: string;
  name: string;
  phone: string;
}

const DEFAULT_ACCOUNT_FILTERS: AccountFilters = { id: '', name: '', phone: '' };

const RoleAccountsDrawer: React.FC<RoleAccountsDrawerProps> = ({ visible, roleId, roleName, onClose }) => {
  const { t } = useTranslation();
  const containerClassName = useFilterContainerClassName();
  const inputContainerClassName = useFilterInputContainerClassName();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [draftFilters, setDraftFilters] = useState<AccountFilters>(DEFAULT_ACCOUNT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<AccountFilters>(DEFAULT_ACCOUNT_FILTERS);

  // Drawer 组件本身不会随 open/close 卸载（父组件始终渲染，只切换 visible），
  // 分页/筛选状态需要在每次打开时手动重置为初始值，否则会带着上一次查看的角色的状态。
  useEffect(() => {
    if (visible) {
      setCurrentPage(1);
      setPageSize(10);
      setDraftFilters(DEFAULT_ACCOUNT_FILTERS);
      setAppliedFilters(DEFAULT_ACCOUNT_FILTERS);
    }
  }, [visible]);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({ ...draftFilters });
  };

  const handleResetFilters = () => {
    setCurrentPage(1);
    setDraftFilters(DEFAULT_ACCOUNT_FILTERS);
    setAppliedFilters(DEFAULT_ACCOUNT_FILTERS);
  };

  const variables: QueryAccountByRolePageVariables = useMemo(() => {
    const idTrim = appliedFilters.id.trim();
    const idNum = idTrim ? Number(idTrim) : NaN;
    return {
      roleId: Number(roleId),
      condition: {
        id: Number.isFinite(idNum) && idNum > 0 ? idNum : undefined,
        name: appliedFilters.name.trim() || undefined,
        phone: appliedFilters.phone.trim() || undefined,
      },
      pageRequest: {
        page: currentPage,
        size: pageSize,
        sortDirection: 'DESC',
      },
    };
  }, [roleId, appliedFilters, currentPage, pageSize]);

  const { data, loading } = useQuery<QueryAccountByRolePageResponse, QueryAccountByRolePageVariables>(
    QUERY_ACCOUNT_BY_ROLE_PAGE,
    {
      variables,
      skip: !visible || !roleId,
      fetchPolicy: 'network-only',
    }
  );

  const pageData = data?.queryAccountByRolePage;

  const formatPhone = (record: OpdsAccountType) => {
    if (record.phone == null && record.countryCode == null) return '-';
    const cc = record.countryCode != null ? `+${record.countryCode} ` : '';
    const num = record.phone != null ? String(record.phone) : '';
    return `${cc}${num}`.trim() || '-';
  };

  const getStatusLabel = (status: number | null | undefined) => {
    if (status === 1) return t('central.platformPermission.personnel.filters.statusActive');
    if (status === 0) return t('central.platformPermission.personnel.filters.statusInactive');
    return '-';
  };

  const columns: TableColumnsType<OpdsAccountType> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 100,
      },
      {
        title: t('central.personnel.columns.name'),
        dataIndex: 'name',
        key: 'name',
        width: 170,
        render: (v) => v || '-',
      },
      {
        title: t('central.personnel.columns.phone'),
        key: 'phone',
        width: 160,
        render: (_: unknown, record: OpdsAccountType) => formatPhone(record),
      },
      {
        title: t('central.platformPermission.role.accountsDrawer.table.status'),
        dataIndex: 'status',
        key: 'status',
        width: 80,
        render: (status: number | null | undefined) => getStatusLabel(status),
      },
    ],
    [t]
  );

  return (
    <Drawer
      title={t('central.platformPermission.role.accountsDrawer.title', { name: roleName })}
      open={visible}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      <div className="mb-2">
        <div className="grid grid-cols-3 gap-x-6 gap-y-3 items-start">
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(['ID'])}>ID</span>
              <div className={inputContainerClassName}>
                <Input
                  value={draftFilters.id}
                  onChange={(e) => setDraftFilters({ ...draftFilters, id: e.target.value })}
                  allowClear
                  size="middle"
                  className="w-full"
                  placeholder={t('components.input.typeIn') as string}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName([String(t('central.personnel.columns.name'))])}>
                {t('central.personnel.columns.name')}
              </span>
              <div className={inputContainerClassName}>
                <Input
                  value={draftFilters.name}
                  onChange={(e) => setDraftFilters({ ...draftFilters, name: e.target.value })}
                  allowClear
                  size="middle"
                  className="w-full"
                  placeholder={t('components.input.typeIn') as string}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName([String(t('central.personnel.columns.phone'))])}>
                {t('central.personnel.columns.phone')}
              </span>
              <div className={inputContainerClassName}>
                <Input
                  value={draftFilters.phone}
                  onChange={(e) => setDraftFilters({ ...draftFilters, phone: e.target.value })}
                  allowClear
                  size="middle"
                  className="w-full"
                  placeholder={t('components.input.typeIn') as string}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button type="primary" onClick={handleApplyFilters}>
            {t('components.button.filter')}
          </Button>
          <Button onClick={handleResetFilters}>{t('components.button.reset')}</Button>
        </div>
      </div>

      <Table<OpdsAccountType>
        rowKey={(r) => String(r.id)}
        loading={loading}
        columns={columns}
        dataSource={pageData?.content ?? []}
        pagination={false}
        size="middle"
        tableLayout="fixed"
        className="mb-4"
      />

      <TablePaginationBar
        total={pageData?.totalElements ?? 0}
        current={currentPage}
        pageSize={pageSize}
        onChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
        onShowSizeChange={(_, size) => {
          setCurrentPage(1);
          setPageSize(size);
        }}
        pageSizeOptions={['10', '20', '50', '100']}
      />
    </Drawer>
  );
};

export default RoleAccountsDrawer;
